<script setup lang="ts">
import { Motion } from "motion-v"
import type { HTMLAttributes } from "vue"
import { computed } from "vue"
import { ThemeImg } from "~/components/theme"
import { useLocation } from "~/composables/locations"
import { cn } from "~/lib/utils"

const { class: cl } = defineProps<{
    class?: HTMLAttributes["class"]
}>()

const { location } = useLocation()

const images = computed(() => {
    const gallery = location.value?.pageContent?.gallery as any
    // Handle both legacy array format and new object format
    if (Array.isArray(gallery)) return gallery
    return gallery?.items || []
})
const key = computed(() => `${location.value?.name}-gallery`)
</script>

<template>
    <section
        id="gallery"
        :class="cn('space-y-8 md:space-y-10', cl)"
    >
        <Motion
            :key="key"
            class="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 auto-rows-[min(160px,40vw)] sm:auto-rows-[min(180px,25vw)] mx-auto"
            initial="hidden"
            while-in-view="visible"
            :in-view-options="{ once: true }"
            :transition="{ 
                staggerChildren: 0.25
            }"
        >
            <Motion
                v-for="image of images"
                :key="image.path"
                :class="cn('size-full scroll-mb-20 group overflow-hidden shadow-md md:scroll-mb-0', image.class)"
                :variants="{ 
                    hidden: { y: 25, opacity: 0 },
                    visible: { y: 0, opacity: 1 }
                }"
                :transition="{
                    type: 'spring',
                    duration: 1.5
                }"
            >
                <ThemeImg
                    :src="image.path"
                    :alt="image.alt || ''"
                    :class="cn('block size-full object-cover transition-transform duration-300 group-hover:scale-105')"
                    :style="image.objectPositionStyle"
                    loading="lazy"
                />
            </Motion>
        </Motion>
    </section>
</template>

<style scoped>
/* Custom grid row span classes */
.grid-row-2 {
    grid-row: span 2 / span 2;
}

.grid-row-3 {
    grid-row: span 3 / span 3;
}

.grid-col-2 {
    grid-column: span 2 / span 2;
}

/* Responsive variants */
@media (min-width: 640px) {
    .sm\:grid-row-2 {
        grid-row: span 2 / span 2;
    }
    
    .sm\:grid-row-3 {
        grid-row: span 3 / span 3;
    }
    
    .sm\:grid-col-2 {
        grid-column: span 2 / span 2;
    }
}

@media (min-width: 768px) {
    .md\:grid-row-2 {
        grid-row: span 2 / span 2;
    }
    
    .md\:grid-row-3 {
        grid-row: span 3 / span 3;
    }
    
    .md\:grid-col-2 {
        grid-column: span 2 / span 2;
    }
}
</style>
